import { useState, type FormEvent } from 'react'
import { FlaskConical, Loader2, Rocket, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { SimulationView } from '@/components/campaigns/SimulationView'
import { formatDay, formatNumber, sumValues, todayInMadrid } from '@/components/campaigns/campaignFormat'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  useAudienceConsentQuery,
  useCampaignSettingsQuery,
  useLaunchCampaign,
  useSimulateCampaign,
  useTestSendCampaign,
  useWabaTemplatesQuery,
} from '@/queries'
import type { CrmCampaign } from '@/services/crmCampaignsService'

/** Paso 4 del editor: ver qué saldría, probarlo en el móvil propio y lanzar. */
export function ReviewStep({ campaign }: { campaign: CrmCampaign }) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const navigate = useNavigate()
  const consent = useAudienceConsentQuery(campaign.id)
  const settings = useCampaignSettingsQuery()
  const templates = useWabaTemplatesQuery(settings.data?.waba_id)
  const simulate = useSimulateCampaign(campaign.id)
  const testSend = useTestSendCampaign(campaign.id)
  const launch = useLaunchCampaign(campaign.id)

  const today = todayInMadrid()
  const [startDate, setStartDate] = useState(today)
  const [confirming, setConfirming] = useState(false)
  const [testStep, setTestStep] = useState('1')
  const [testPhone, setTestPhone] = useState('')

  const consentData = consent.data
  const discarded = sumValues(consentData?.descartados_por_motivo)
  const sendable = (consentData?.total ?? 0) - discarded
  const consentDone = consentData?.estado === 'hecho'
  // Hoy no se manda: el servidor empieza entonces en el minuto siguiente, no a las 00:00.
  const startParam = startDate && startDate !== today ? startDate : undefined

  const blocker = !campaign.steps.length
    ? t('campaigns.review.blocked_steps')
    : consent.isError
      ? t('campaigns.review.blocked_consent_error')
      : !consentDone
        ? t('campaigns.review.blocked_consent')
        : sendable <= 0
          ? t('campaigns.review.blocked_recipients')
          : null

  const submitTest = (event: FormEvent) => {
    event.preventDefault()
    if (!testPhone.trim()) return
    testSend.mutate(
      { step: Number(testStep), phone: testPhone.trim() },
      {
        onSuccess: () => toast.success(t('campaigns.review.test_sent', { phone: testPhone.trim() })),
        onError: (error) => toast.error(error.message),
      },
    )
  }

  const confirmLaunch = () => {
    launch.mutate(startParam, {
      onSuccess: () => {
        toast.success(t('campaigns.review.launched', { count: formatNumber(sendable, lang) }))
        navigate(`/${lang}/campaigns/${campaign.id}`)
      },
      onError: () => setConfirming(false),
    })
  }

  return (
    <div className="space-y-8">
      <dl className="flex flex-wrap items-baseline gap-x-8 gap-y-2 text-sm">
        <div className="flex items-baseline gap-2">
          <dd className="text-2xl font-semibold tabular-nums">{formatNumber(sendable > 0 ? sendable : 0, lang)}</dd>
          <dt className="text-muted-foreground">{t('campaigns.review.recipients')}</dt>
        </div>
        <div className="flex items-baseline gap-2">
          <dd className="text-2xl font-semibold tabular-nums">{formatNumber(discarded, lang)}</dd>
          <dt className="text-muted-foreground">{t('campaigns.review.discarded')}</dt>
        </div>
        <div className="flex items-baseline gap-2">
          <dd className="text-2xl font-semibold tabular-nums">{campaign.steps.length}</dd>
          <dt className="text-muted-foreground">{t('campaigns.review.steps')}</dt>
        </div>
      </dl>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t('campaigns.review.simulation_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('campaigns.review.simulation_help')}</p>
        <Button
          type="button"
          variant="outline"
          disabled={simulate.isPending || !campaign.steps.length}
          onClick={() => simulate.mutate(startParam)}
        >
          {simulate.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FlaskConical className="mr-2 h-4 w-4" />}
          {t('campaigns.review.simulate')}
        </Button>
        {simulate.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{simulate.error.message}</AlertDescription>
          </Alert>
        ) : null}
        {simulate.data ? <SimulationView simulation={simulate.data} templates={templates.data ?? []} /> : null}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t('campaigns.review.test_title')}</h2>
        <form className="flex flex-wrap items-end gap-2" onSubmit={submitTest}>
          <div className="space-y-1.5">
            <Label htmlFor="test-step">{t('campaigns.review.test_step')}</Label>
            <Select value={testStep} onValueChange={setTestStep}>
              <SelectTrigger id="test-step" className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {campaign.steps.map((step) => (
                  <SelectItem key={step.orden} value={String(step.orden)}>
                    {t('campaigns.steps.step_title', { number: step.orden })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="test-phone">{t('campaigns.review.test_phone')}</Label>
            <Input
              id="test-phone"
              aria-label={t('campaigns.review.test_phone')}
              className="w-52"
              type="tel"
              placeholder="+34 600 00 00 00"
              value={testPhone}
              onChange={(event) => setTestPhone(event.target.value)}
            />
          </div>
          <Button type="submit" variant="outline" disabled={testSend.isPending || !testPhone.trim() || !campaign.steps.length}>
            {testSend.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {t('campaigns.review.test_send')}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground">{t('campaigns.review.test_help')}</p>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t('campaigns.review.launch_title')}</h2>
        <div className="space-y-1.5">
          <Label htmlFor="launch-date">{t('campaigns.review.start_date')}</Label>
          <Input
            id="launch-date"
            aria-label={t('campaigns.review.start_date')}
            className="w-44"
            type="date"
            min={today}
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </div>
        {launch.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{launch.error.message}</AlertDescription>
          </Alert>
        ) : null}
        <div className="space-y-2">
          <Button type="button" disabled={Boolean(blocker) || launch.isPending} onClick={() => setConfirming(true)}>
            <Rocket className="mr-2 h-4 w-4" />
            {t('campaigns.review.launch', { count: formatNumber(sendable > 0 ? sendable : 0, lang) })}
          </Button>
          {blocker ? (
            <p className="text-xs text-muted-foreground" data-testid="launch-blocker">
              {blocker}
            </p>
          ) : null}
        </div>
      </section>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('campaigns.review.confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription data-testid="launch-confirm-text">
              {t('campaigns.review.confirm_description', {
                count: formatNumber(sendable, lang),
                discarded: formatNumber(discarded, lang),
                date: formatDay(`${startDate || today}T12:00:00Z`, lang),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('campaigns.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={launch.isPending} onClick={confirmLaunch}>
              {t('campaigns.review.confirm_action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
